import { GzCheckboxWebComponent, GzEnumListWebComponent } from '../../../index';
import { EnumListEvents, EnumListItem, GroupType, SelectedOption } from '../../gzEnumList/types';
import { ENDPOINT_LIST_ID, EndpointListEvents } from '../types';
import { html, PropertyValues, TemplateResult } from 'lit';
import { query } from 'lit/decorators.js';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { debounce } from '../../../utils/event.utils';
import { ElementClass } from '../../../utils/htmlElement.utils';
import { styles } from '../view/styles';
import { EndpointSpecialValues } from '@nirvana/shared/dtos/types/shared/filters';

export class GzEndpointListWebComponent extends GzEnumListWebComponent {
  private _isLoading = false;

  @query('#endpoint-loader')
  private loader;

  static get styles() {
    return styles;
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('filterOptions') && this.listIsFiltered) {
      super.handleOptionsSearch(
        new CustomEvent('search', {
          detail: {
            sourceEvent: this.searchInput.value,
          },
        })
      );
    }
  }

  protected shouldUpdate(changedProperties): boolean {
    return !(this.groupType === GroupType.DEFAULT && changedProperties.get('listIsFiltered') === false);
  }

  public getNumberOfVisibleEndpoints() {
    return Array.from(this.optionsList).filter((option: Element) => !option.classList.contains(ElementClass.HIDDEN))
      .length;
  }

  public getCheckedOptions(): SelectedOption[] {
    const checkedValues: SelectedOption[] = [];

    this.checkboxesList.forEach((checkbox: GzCheckboxWebComponent) => {
      if (checkbox.checked) {
        const { label, value } = checkbox;
        checkedValues.push({ label, value: value });
      }
    });

    return checkedValues;
  }

  public getSelectedValuesFromOptionsList(): SelectedOption[] {
    return this.getCheckedOptions();
  }

  public getSelectedValuesFromFilterOptions(): SelectedOption[] {
    return super.getSelectedValuesFromOptionsList();
  }

  protected renderSearchInput() {
    return html` <gz-input-search
      placeholder="${this.searchPlaceholderString}"
      inputWidth="medium"
      .disabled="${isEmpty(this.filterOptions)}"
      @change="${debounce(this.handleOptionsSearch, 700)}"
    ></gz-input-search>`;
  }

  protected renderListOptions() {
    return html` <div id="${ENDPOINT_LIST_ID}" class="list--options">
      <div id="endpoint-loader" class="loader-hidden">${this.renderLoadingTemplate()}</div>
      ${this.renderSelectAllItemsCheckbox()} ${this.renderFilterOptionsList()} ${this.renderNoResultsTemplate()}
    </div>`;
  }

  private renderLoadingTemplate() {
    return html` <div class="spinner">
      <gz-progress-spinner spinnerwidth="medium"></gz-progress-spinner>
    </div>`;
  }

  protected matchCheckbox(checkbox: GzCheckboxWebComponent, selectedOption: SelectedOption) {
    return selectedOption.value === checkbox.value;
  }

  protected handleOptionsSearch(e: CustomEvent) {
    const searchString = e.detail.sourceEvent;

    if (searchString === '') {
      this.dispatchEvent(
        new CustomEvent(EndpointListEvents.SEARCH_CLEAR, {
          detail: {
            sourceEvent: e,
          },
          bubbles: true,
          composed: true,
        })
      );
      return;
    }

    this.dispatchEvent(
      new CustomEvent(EndpointListEvents.SEARCH_ENDPOINT, {
        detail: {
          searchString,
          sourceEvent: e,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  public handleCheckboxChange(option: EnumListItem, e: CustomEvent) {
    this.unselectSpecialOptions();
    super.handleCheckboxChange(option, e);
  }

  protected hasSearchInput(): boolean {
    return true;
  }

  public continueHandleOptionsSearch(sourceEvent: CustomEvent) {
    super.handleOptionsSearch(sourceEvent);
  }

  protected getFilteredResults(searchString) {
    return this.filterOptions.filter((option: EnumListItem) => {
      return option.label.toLowerCase().startsWith(searchString);
    });
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  set isLoading(value: boolean) {
    if (value && !this._isLoading) {
      this.loader.classList.remove('loader-hidden');
    }

    if (!value && this._isLoading) {
      this.loader.classList.add('loader-hidden');
    }

    this._isLoading = value;
  }

  protected renderSelectAllItemsCheckbox(): TemplateResult | undefined {
    if (!this.canSelectAll) {
      return;
    }

    return html`
      <div id="allCheckboxesSelect" class="option option-all">
        <gz-checkbox
          class="is-filterable full-checkbox"
          value="${EndpointSpecialValues.ALL}"
          label="${this.allItemsLabel}"
          ?checked="${false}"
          @change="${this.handleExclusiveSelection}"
        ></gz-checkbox>
      </div>
    `;
  }

  private handleExclusiveSelection(event) {
    const value = event.detail.value;
    this.selectOnlyValue(value);
    this.emitFilterOptionToggledEvent();
  }

  private selectOnlyValue(value: string) {
    this.checkboxesList.forEach((checkbox: GzCheckboxWebComponent) => {
      checkbox.checked = checkbox.value === value;
    });

    this.filterOptions = this.filterOptions.map((item: EnumListItem) => {
      if (item.value === value) {
        return { ...item, selected: true };
      }

      return { ...item, selected: false };
    });
  }

  protected emitFilterOptionToggledEvent() {
    this.dispatchEvent(
      new CustomEvent(EnumListEvents.OPTION_CHANGED, {
        bubbles: true,
        composed: true,
      })
    );
  }

  public unselectSpecialOptions() {
    this.checkboxesList
      .filter((checkbox: GzCheckboxWebComponent) => checkbox.value === EndpointSpecialValues.ALL)
      .forEach((checkbox: GzCheckboxWebComponent) => {
        checkbox.checked = false;
      });
  }

  protected updateFilteredCheckboxesBySearchResult(searchResult: EnumListItem[]) {
    if (this.filterOptions.length !== this.optionsList.length) {
      throw Error('Unable to sync endpoint options in selector!');
    }

    super.updateFilteredCheckboxesBySearchResult(searchResult);
  }
}
